import { useState } from "react";
import { 
  CheckCircle2, Circle, AlertTriangle, Link2, Trash2, Plus, 
  FileText, Calendar, Eye 
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { format, isPast, isValid } from "date-fns";
import { fr } from "date-fns/locale";
import { 
  usePropertyDocuments, 
  PROPERTY_DOC_TYPES, 
  getRequiredDocs,
  type PropertyDocument 
} from "@/hooks/useProperties";
import { UnifiedDocumentLinkDialog } from "@/components/shared/UnifiedDocumentLinkDialog";
import { supabase } from "@/integrations/supabase/client";
import { useDocumentPreview } from "@/contexts/DocumentPreviewContext";

interface PropertyChecklistProps {
  propertyId: string;
  transactionType: "sale" | "rental";
}

export function PropertyChecklist({ propertyId, transactionType }: PropertyChecklistProps) {
  const { documents, isLoading, addDocument, deleteDocument, linkDocument } = usePropertyDocuments(propertyId);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [selectedDocType, setSelectedDocType] = useState<string | null>(null);
  const [selectedDocId, setSelectedDocId] = useState<string | null>(null);
  const { openDocument } = useDocumentPreview();

  // Use standard DocumentPreviewSheet via context
  const handleViewDocument = async (documentId: string) => {
    try {
      const { data, error } = await supabase
        .from("documents")
        .select("*")
        .eq("id", documentId)
        .single();

      if (error) throw error;
      if (data) {
        openDocument(data);
      }
    } catch (error) {
      console.error("Error fetching document:", error);
    }
  };

  const requiredDocs = getRequiredDocs(transactionType);
  
  // Get all doc types for this transaction type (required + optional that are added)
  const allDocTypes = [...new Set([
    ...requiredDocs,
    ...documents.map(d => d.doc_type)
  ])];

  // Create a map of doc_type to document
  const docMap = new Map<string, PropertyDocument>();
  documents.forEach(d => docMap.set(d.doc_type, d));

  // Calculate completion stats
  const requiredCompleted = requiredDocs.filter(dt => {
    const doc = docMap.get(dt);
    return doc?.status === "valid";
  }).length;
  const completionPercent = requiredDocs.length > 0 
    ? Math.round((requiredCompleted / requiredDocs.length) * 100) 
    : 100;

  const handleAddMissingDocs = async () => {
    // Add all required docs that don't exist yet
    for (const docType of requiredDocs) {
      if (!docMap.has(docType)) {
        await addDocument.mutateAsync({
          property_id: propertyId,
          doc_type: docType,
          label: PROPERTY_DOC_TYPES[docType]?.label || docType,
          status: "pending",
          document_id: null,
          valid_from: null,
          expires_at: null,
          notes: null,
        });
      }
    }
  };

  const handleLinkClick = (docType: string, docId: string) => {
    setSelectedDocType(docType);
    setSelectedDocId(docId);
    setLinkDialogOpen(true);
  };

  const handleLinkSuccess = () => {
    setLinkDialogOpen(false);
    setSelectedDocType(null);
    setSelectedDocId(null);
  };

  const getStatusIcon = (doc: PropertyDocument | undefined, isRequired: boolean) => {
    if (!doc) {
      return isRequired 
        ? <AlertTriangle className="h-4 w-4 text-amber-500" />
        : <Circle className="h-4 w-4 text-muted-foreground" />;
    }
    
    if (doc.status === "valid") {
      // Check if expired
      if (doc.expires_at && isPast(new Date(doc.expires_at))) {
        return <AlertTriangle className="h-4 w-4 text-red-500" />;
      }
      return <CheckCircle2 className="h-4 w-4 text-green-500" />;
    }
    
    if (doc.status === "expired") {
      return <AlertTriangle className="h-4 w-4 text-red-500" />;
    }
    
    return <Circle className="h-4 w-4 text-muted-foreground" />;
  };

  const getStatusBadge = (doc: PropertyDocument | undefined) => {
    if (!doc) return <Badge variant="outline" className="text-xs">Manquant</Badge>;
    
    if (doc.status === "valid") {
      if (doc.expires_at && isPast(new Date(doc.expires_at))) {
        return <Badge variant="destructive" className="text-xs">Expiré</Badge>;
      }
      return <Badge className="bg-green-500/10 text-green-600 text-xs">Valide</Badge>;
    }
    
    if (doc.status === "expired") {
      return <Badge variant="destructive" className="text-xs">Expiré</Badge>;
    }
    
    return <Badge variant="secondary" className="text-xs">En attente</Badge>;
  };

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-3">
        {[1, 2, 3].map(i => (
          <div key={i} className="h-12 bg-muted rounded-lg" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with progress */}
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <h3 className="font-semibold">Documents du dossier</h3>
          <p className="text-sm text-muted-foreground">
            {requiredCompleted}/{requiredDocs.length} documents obligatoires
          </p>
        </div>
        <div className="text-right">
          <div className={cn(
            "text-2xl font-bold",
            completionPercent >= 80 ? "text-green-600" :
            completionPercent >= 50 ? "text-amber-600" : "text-red-600"
          )}>
            {completionPercent}%
          </div>
        </div>
      </div>

      {/* Progress bar */}
      <div className="relative h-2 bg-muted rounded-full overflow-hidden">
        <div 
          className={cn(
            "absolute left-0 top-0 h-full rounded-full transition-all",
            completionPercent >= 80 ? "bg-green-500" :
            completionPercent >= 50 ? "bg-amber-500" : "bg-red-500"
          )}
          style={{ width: `${completionPercent}%` }}
        />
      </div>

      {/* Initialize checklist button */}
      {documents.length === 0 && (
        <Button onClick={handleAddMissingDocs} className="w-full">
          <Plus className="mr-2 h-4 w-4" />
          Initialiser la checklist ({requiredDocs.length} documents)
        </Button>
      )}

      {/* Document list */}
      <div className="space-y-2">
        {allDocTypes.map(docType => {
          const doc = docMap.get(docType);
          const config = PROPERTY_DOC_TYPES[docType];
          const isRequired = requiredDocs.includes(docType);
          const label = config?.label || doc?.label || docType;

          return (
            <div
              key={docType}
              className={cn(
                "flex items-center gap-3 p-3 rounded-lg border transition-colors",
                doc?.status === "valid" ? "bg-green-50/50 border-green-200" :
                !doc && isRequired ? "bg-amber-50/50 border-amber-200" :
                "bg-card border-border"
              )}
            >
              {/* Status icon */}
              {getStatusIcon(doc, isRequired)}

              {/* Label and info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-sm truncate">{label}</span>
                  {isRequired && (
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                      Obligatoire
                    </Badge>
                  )}
                </div>
                
                {doc?.linked_document && (
                  <div className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5">
                    <FileText className="h-3 w-3" />
                    <span className="truncate">{doc.linked_document.title}</span>
                  </div>
                )}
                
                {doc?.expires_at && isValid(new Date(doc.expires_at)) && (
                  <div className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5">
                    <Calendar className="h-3 w-3" />
                    <span>Expire le {format(new Date(doc.expires_at), "dd MMM yyyy", { locale: fr })}</span>
                  </div>
                )}
              </div>

              {/* Status badge */}
              {getStatusBadge(doc)}

              {/* Actions */}
              <div className="flex items-center gap-1">
                {doc ? (
                  <>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => handleLinkClick(docType, doc.id)}
                    >
                      <Link2 className="h-4 w-4" />
                    </Button>
                    {doc.document_id && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => handleViewDocument(doc.document_id!)}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive hover:text-destructive"
                      onClick={() => deleteDocument.mutate(doc.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={async () => {
                      const result = await addDocument.mutateAsync({
                        property_id: propertyId,
                        doc_type: docType,
                        label,
                        status: "pending",
                        document_id: null,
                        valid_from: null,
                        expires_at: null,
                        notes: null,
                      });
                      handleLinkClick(docType, result.id);
                    }}
                  >
                    <Plus className="mr-1 h-3 w-3" />
                    Ajouter
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Link dialog */}
      {selectedDocType && (
        <UnifiedDocumentLinkDialog
          open={linkDialogOpen}
          onOpenChange={setLinkDialogOpen}
          entityType="property"
          entityId={propertyId}
          docConfig={{
            key: selectedDocType,
            label: PROPERTY_DOC_TYPES[selectedDocType]?.label || selectedDocType,
          }}
          linkTableName="property_documents"
          onSuccess={handleLinkSuccess}
        />
      )}
    </div>
  );
}
